import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, FileText, ArrowLeft } from 'lucide-react';
import SignaturePad from '../../components/SignaturePad';
import { 
  getLiabilityWaiverByToken, 
  submitLiabilityWaiver, 
  type LiabilityWaiver 
} from '../../lib/liabilityWaivers';
import { 
  getDamageReportByBookingId, 
  type VehicleDamageReport,
  DAMAGE_TYPES,
  SEVERITY_LEVELS
} from '../../lib/damageReports';

export default function LiabilityWaiverPage() {
  const { token } = useParams<{ token: string }>();
  const [waiver, setWaiver] = useState<LiabilityWaiver | null>(null);
  const [damageReport, setDamageReport] = useState<VehicleDamageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Detect PWA/mobile environment for back button
  const [showBackButton, setShowBackButton] = useState(false);

  useEffect(() => {
    loadWaiver();
    
    // Detect if user likely came from PWA/mobile redirect
    const isMobile = window.innerWidth < 768;
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  (window.navigator as any).standalone || 
                  document.referrer.includes('android-app://');
    const hasHistory = window.history.length > 1;
    
    // Show back button for mobile/PWA users who have browser history
    setShowBackButton((isMobile || isPWA) && hasHistory);
  }, [token]);

  const loadWaiver = async () => {
    if (!token) {
      setError('Invalid waiver link');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const waiverData = await getLiabilityWaiverByToken(token);
      
      if (!waiverData) {
        setError('Waiver not found or expired');
        return;
      }

      if (waiverData.agreed_at) {
        setSubmitted(true);
      }

      setWaiver(waiverData);

      // Load damage report if booking_id exists
      if (waiverData.booking_id) {
        try {
          const damage = await getDamageReportByBookingId(waiverData.booking_id);
          setDamageReport(damage);
        } catch (err) {
          console.warn('Could not load damage report:', err);
          // Don't set error as damage report is optional
        }
      }
    } catch (err) {
      console.error('Error loading waiver:', err);
      setError('Failed to load waiver. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!waiver || !signatureData) {
      setError('Please provide your signature');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Get client IP and user agent
      const userAgent = navigator.userAgent;
      
      const success = await submitLiabilityWaiver(
        token!,
        signatureData,
        undefined, // IP will be detected server-side
        userAgent
      );

      if (success) {
        setSubmitted(true);
      } else {
        setError('Failed to submit waiver. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting waiver:', err);
      setError('Failed to submit waiver. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoBack = () => {
    try {
      // Check if there's actual history to go back to
      if (window.history.length > 1) {
        window.history.back();
      } else {
        // Fallback: close window (if opened in popup) or try to go to origin
        if (window.opener) {
          window.close();
        } else {
          window.location.href = window.location.origin;
        }
      }
    } catch (err) {
      console.error('Error navigating back:', err);
      // Ultimate fallback: refresh to origin
      window.location.href = window.location.origin;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">Loading Waiver</h2>
          <p className="text-sm text-gray-500">Please wait while we load your liability waiver...</p>
        </div>
      </div>
    );
  }

  if (error && !waiver) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <AlertCircle className="h-8 w-8 mx-auto mb-4 text-red-600" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">Error</h2>
          <p className="text-sm text-gray-500 mb-4">{error}</p>
          <button
            onClick={loadWaiver}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <CheckCircle className="h-8 w-8 mx-auto mb-4 text-green-600" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">Waiver Completed</h2>
          <p className="text-sm text-gray-500 mb-4">
            Thank you! Your liability waiver has been successfully signed and submitted.
          </p>
          
          {/* Back button for PWA/mobile users */}
          {showBackButton && (
            <button
              onClick={handleGoBack}
              className="mb-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Return to App
            </button>
          )}
          
          <p className="text-xs text-gray-400">
            {showBackButton ? 'Or you can close this page.' : 'You can now close this page.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 text-white p-6 relative">
            {/* Back Button - for PWA/mobile users */}
            {showBackButton && (
              <button
                onClick={handleGoBack}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 hover:bg-blue-700 rounded-full transition-colors"
                title="Go back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            
            {/* Centered content */}
            <div className="text-center">
              <FileText className="h-8 w-8 mx-auto mb-3" />
              <h1 className="text-xl font-bold">Liability Waiver</h1>
              <p className="text-blue-100 text-sm mt-1">
                Please read carefully and sign below
              </p>
            </div>
          </div>

          {/* Customer Info */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <h2 className="font-medium text-gray-900 mb-2">Customer Information</h2>
            <div className="text-sm text-gray-600">
              <p><strong>Name:</strong> {waiver?.customer_name}</p>
              {waiver?.customer_email && (
                <p><strong>Email:</strong> {waiver.customer_email}</p>
              )}
              {waiver?.customer_phone && (
                <p><strong>Phone:</strong> {waiver.customer_phone}</p>
              )}
            </div>
          </div>

          {/* Vehicle Damage Report */}
          {damageReport && !submitted && !waiver?.agreed_at && (
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <h2 className="font-medium text-gray-900 mb-4">Pre-existing Vehicle Damage Report</h2>
              
              {/* Vehicle Info */}
              {(damageReport.vehicle_make || damageReport.vehicle_model) && (
                <div className="mb-4 p-3 bg-white rounded border">
                  <h3 className="font-medium text-gray-700 mb-2">Vehicle Information</h3>
                  <div className="text-sm text-gray-600">
                    {damageReport.vehicle_year && <span>{damageReport.vehicle_year} </span>}
                    {damageReport.vehicle_make && <span>{damageReport.vehicle_make} </span>}
                    {damageReport.vehicle_model && <span>{damageReport.vehicle_model}</span>}
                    {damageReport.vehicle_color && <span className="block">Color: {damageReport.vehicle_color}</span>}
                    {damageReport.license_plate && <span className="block">License Plate: {damageReport.license_plate}</span>}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Vehicle Diagram */}
                <div className="lg:col-span-2">
                  <h3 className="font-medium text-gray-900 mb-3">Vehicle Diagram</h3>
                  <div className="relative border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                    <div className="relative w-full h-[400px]">
                      {/* Professional Vehicle Damage Report - Using User's Image */}
                      <img
                        src="/cardiagram.png.jpg"
                        alt="Vehicle Damage Report Diagram"
                        className="w-full h-full object-contain"
                        draggable={false}
                      />

                      {/* Damage Points (Read-only) */}
                      {damageReport.damage_points.map((point, index) => {
                        const typeConfig = DAMAGE_TYPES.find(type => type.value === point.damageType);
                        const pointNumber = index + 1;
                        
                        return (
                          <div
                            key={point.id}
                            className="absolute w-6 h-6 rounded-full border-2 border-gray-800 flex items-center justify-center text-white text-xs font-bold transform -translate-x-3 -translate-y-3"
                            style={{
                              left: `${point.x}%`,
                              top: `${point.y}%`,
                              backgroundColor: typeConfig?.color || '#a0a0a0'
                            }}
                            title={`#${pointNumber}: ${typeConfig?.label} (${point.severity})${point.notes ? `: ${point.notes}` : ''}`}
                          >
                            {pointNumber}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Damage Points Summary */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">
                    Documented Damage ({damageReport.damage_points.length} points)
                  </h3>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {damageReport.damage_points.map((point, index) => {
                      const typeConfig = DAMAGE_TYPES.find(type => type.value === point.damageType);
                      const severityConfig = SEVERITY_LEVELS.find(level => level.value === point.severity);
                      const pointNumber = index + 1;
                      
                      return (
                        <div
                          key={point.id}
                          className="flex items-start space-x-3 p-2 bg-white rounded border"
                        >
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold border border-gray-600 flex-shrink-0"
                            style={{ backgroundColor: typeConfig?.color || '#a0a0a0' }}
                          >
                            {pointNumber}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 flex-wrap">
                              <span className="font-medium text-sm">{typeConfig?.label}</span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                point.severity === 'minor' ? 'bg-yellow-100 text-yellow-800' :
                                point.severity === 'moderate' ? 'bg-orange-100 text-orange-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {severityConfig?.label}
                              </span>
                            </div>
                            {point.notes && (
                              <p className="text-xs text-gray-600 mt-1">
                                {point.notes}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {damageReport.additional_notes && (
                    <div className="mt-4 p-3 bg-white rounded border">
                      <h4 className="font-medium text-gray-700 text-sm mb-2">Additional Notes</h4>
                      <p className="text-xs text-gray-600">{damageReport.additional_notes}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Important:</strong> By signing this waiver, you acknowledge that the above damage was present before service began and that our company is not responsible for these pre-existing conditions.
                </p>
              </div>
            </div>
          )}

          {/* Terms Content */}
          <div className="p-6">
            <h2 className="font-medium text-gray-900 mb-4">Terms and Conditions</h2>
            <div className="bg-gray-50 p-4 rounded-md border max-h-64 overflow-y-auto">
              <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {waiver?.terms_content}
              </div>
            </div>



            {/* Signature Section */}
            <div className="mt-6">
              <h3 className="font-medium text-gray-900 mb-3">Digital Signature</h3>
              <p className="text-sm text-gray-600 mb-4">
                Please sign in the box below using your mouse, finger, or stylus:
              </p>
              
              <div className="flex justify-center mb-4">
                <SignaturePad
                  onSignatureChange={setSignatureData}
                  width={400}
                  height={150}
                  className="w-full max-w-md"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <div className="text-center">
                <button
                  onClick={handleSubmit}
                  disabled={!signatureData || submitting}
                  className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2 inline" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Signed Waiver'
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                By providing your digital signature, you acknowledge that you have read, understood, 
                and agree to be bound by the terms of this liability waiver.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-xs text-gray-400">
          <p>This waiver is legally binding. Please read carefully before signing.</p>
        </div>
      </div>
    </div>
  );
} 